import { useState } from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Button,
  DialogActions,
  Alert,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import INSPECTION_STATUSES from '@/components/inspectionStatuses';
import type { Inspection } from '@/types/apiTypes';
import type { EntityId } from '@/types/apiTypes';

interface InspectionWithStatus extends Inspection {
  status?: string;
}

interface InspectionDeleteDialogProps {
  inspection: InspectionWithStatus | null | undefined;
  deleteInspection: (inspection_id: EntityId) => Promise<void>;
  open: boolean;
  handleClose: () => void;
}

function InspectionDeleteDialog({
  inspection,
  deleteInspection,
  open,
  handleClose,
}: InspectionDeleteDialogProps): React.JSX.Element {
  const [loading, setLoading] = useState<boolean>(false);
  const isCompleted = inspection?.status === INSPECTION_STATUSES.COMPLETE;

  const handleSubmit = async (): Promise<void> => {
    if (!inspection) return;
    setLoading(true);
    await deleteInspection(inspection.entity_id);
    setLoading(false);
    handleClose();
  };

  return (
    <Dialog onClose={() => handleClose()} open={open}>
      <DialogTitle>
        <Grid container direction='row' alignItems='center'>
          <Grid size={11}>Delete Inspection</Grid>
          <Grid size={1} sx={{ maxWidth: '25px' }}>
            <IconButton aria-label='close' onClick={() => handleClose()}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>
        <Box
          padding='10px'
          sx={{
            display: 'flex',
            maxWidth: 800,
          }}
        >
          <Alert severity={isCompleted ? 'warning' : 'error'}>
            {isCompleted
              ? 'Completed inspections cannot be deleted.'
              : 'Deleting an inspection will permanently delete all inspection check history. This operation is not reversible. Are you sure you want to delete?'}
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
          <Button
            color='inherit'
            onClick={handleClose}
            sx={{ mr: 1, flex: '1 1 auto' }}
          >
            Cancel
          </Button>
          <Box sx={{ flex: '1 1 auto' }} />
          <LoadingButton
            loading={loading}
            variant='contained'
            color='primary'
            onClick={handleSubmit}
            disabled={isCompleted}
          >
            Delete
          </LoadingButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default InspectionDeleteDialog;

